import { onMounted, onBeforeUnmount, ref } from 'vue'
import commons from '@/utils/Common'
import ApiService from '@/services/servicesApi'
import {
  NOTIFICATION_V1_INBOX,
  NOTIFICATION_V1_READ,
  NOTIFICATION_V1_UNREAD,
} from '@/api/const/notification'

export type InboxItem = {
  id: number
  notificationId?: number
  title: string
  content: string
  eventType?: string
  time: string
  createdAt?: string
  read: boolean
  logo?: string
}

const EVENT = 'inbox-notifications-changed'

const asUserRecord = (raw: unknown): Record<string, any> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, any>
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>
      }
    } catch {
      /* ignore */
    }
  }
  return {}
}

const resolveIds = () => {
  const user = asUserRecord(commons.getUserData?.())
  const userId = user.id ?? user.userId ?? user.user_id
  const tenantId = user.tenantId ?? user.tenant_id ?? user.tenantsId ?? 1
  return { userId, tenantId }
}

const unwrapData = (res: any) => res?.data?.data ?? res?.data

const pagingRows = (data: any): any[] => {
  if (!data) return []
  if (Array.isArray(data)) return data
  // ObjectPaging (vn-lib): list nằm ở `body`
  if (Array.isArray(data.body)) return data.body
  if (Array.isArray(data.content)) return data.content
  if (Array.isArray(data.list)) return data.list
  if (Array.isArray(data.records)) return data.records
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.data)) return data.data
  return []
}

const relativeTime = (iso?: string | null) => {
  if (!iso) return ''
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return ''
  const diff = Date.now() - ts
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'Vừa xong'
  if (min < 60) return `${min} phút trước`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour} giờ trước`
  const day = Math.floor(hour / 24)
  if (day < 7) return `${day} ngày trước`
  return new Date(ts).toLocaleDateString('vi-VN')
}

const mapItem = (row: any): InboxItem | null => {
  const id = Number(row.recipientId ?? row.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    notificationId: row.notificationId != null ? Number(row.notificationId) : undefined,
    title: String(row.title || 'Thông báo'),
    content: String(row.content || ''),
    eventType: row.eventType || row.notificationType,
    time: relativeTime(row.createdAt),
    createdAt: row.createdAt,
    read: Boolean(row.isRead ?? row.read),
  }
}

export const notifyInboxChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENT))
  }
}

export function useInboxNotifications() {
  const items = ref<InboxItem[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)

  const fetchUnread = async () => {
    if (!commons.checkLogin?.()) {
      unreadCount.value = 0
      return
    }
    const { userId, tenantId } = resolveIds()
    if (userId == null) return
    try {
      ApiService.init()
      const res: any = await ApiService.getNoParam(
        `${NOTIFICATION_V1_UNREAD}?userId=${userId}&tenantId=${tenantId}`
      )
      const data = unwrapData(res)
      unreadCount.value = Number(data?.unreadCount ?? 0) || 0
    } catch {
      /* keep last */
    }
  }

  const fetchInbox = async () => {
    if (!commons.checkLogin?.()) {
      items.value = []
      unreadCount.value = 0
      return
    }
    const { userId, tenantId } = resolveIds()
    if (userId == null) return
    loading.value = true
    try {
      ApiService.init()
      const res: any = await ApiService.getNoParam(
        `${NOTIFICATION_V1_INBOX}?userId=${userId}&tenantId=${tenantId}&page=1&size=50`
      )
      items.value = pagingRows(unwrapData(res)).map(mapItem).filter(Boolean) as InboxItem[]
      unreadCount.value = items.value.filter((n) => !n.read).length
      if (!items.value.length) {
        await fetchUnread()
      }
    } catch {
      items.value = []
    } finally {
      loading.value = false
    }
  }

  const markRead = async (item: InboxItem) => {
    if (item.read) return
    item.read = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    const { userId } = resolveIds()
    if (userId == null) return
    try {
      ApiService.init()
      await ApiService.postNoParam(`${NOTIFICATION_V1_READ(item.id)}?userId=${userId}`)
    } catch {
      item.read = false
      unreadCount.value += 1
    }
  }

  let poll: ReturnType<typeof setInterval> | null = null
  const onChanged = () => {
    void fetchUnread()
  }

  onMounted(() => {
    void fetchUnread()
    poll = setInterval(() => {
      void fetchUnread()
    }, 20000)
    window.addEventListener(EVENT, onChanged)
  })

  onBeforeUnmount(() => {
    if (poll) clearInterval(poll)
    window.removeEventListener(EVENT, onChanged)
  })

  return {
    items,
    unreadCount,
    loading,
    fetchInbox,
    fetchUnread,
    markRead,
  }
}
